import React from "react";
import { Target, Eye, Heart, Award, Users, Shield, Sparkles } from "lucide-react";

const VALUES = [
  { icon: Shield, t: "Integrity", d: "Transparent processes, honest communication, no shortcuts." },
  { icon: Award, t: "Excellence", d: "Only the best candidates. Only the best clients. Every time." },
  { icon: Heart, t: "Empathy", d: "We listen deeply — to job seekers and to hiring managers." },
  { icon: Sparkles, t: "Innovation", d: "AI-powered matching, modern ATS, and always improving." },
];

const PROCESS = [
  { n: "01", t: "Requirement Deep-Dive", d: "We meet your team to understand role, culture, and success metrics." },
  { n: "02", t: "Talent Mapping", d: "AI + human sourcing across our 5000+ pool and passive candidates." },
  { n: "03", t: "Screening & Shortlist", d: "Skills tests, reference checks, and behavioral interviews." },
  { n: "04", t: "Interview Coordination", d: "We manage scheduling, feedback loops, and offer negotiation." },
  { n: "05", t: "Onboarding & Beyond", d: "90-day replacement guarantee + engagement follow-ups." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-[#0F2A4A] text-white py-24">
        <div className="ae-container">
          <div className="max-w-3xl">
            <div className="ae-overline mb-4">About Achievers Enterprise</div>
            <h1 className="text-4xl md:text-6xl font-light leading-tight">
              Building careers.<br /><span className="font-serif italic text-[#C5A059]">Building companies.</span>
            </h1>
            <p className="mt-6 text-white/80 text-lg leading-relaxed">
              Since our founding, we've placed 5000+ professionals across 300+ organizations — from unicorn startups to Fortune 500 subsidiaries.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="ae-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img src="https://images.pexels.com/photos/7698712/pexels-photo-7698712.jpeg" alt="Team" className="rounded-2xl w-full h-[420px] object-cover" />
          </div>
          <div>
            <div className="ae-overline mb-3">Our Story</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A] mb-6 ae-gold-underline">Rooted in India. Global in ambition.</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Achievers Enterprise was founded on a simple belief — that recruitment shouldn't be transactional. It should be transformational. For the candidate finding purpose. For the company finding growth.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Today we are a full-spectrum HR partner — permanent staffing, executive search, contract hiring, and RPO — trusted by leaders across IT, manufacturing, pharma, HVAC, FMCG, and finance.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="ae-container grid md:grid-cols-2 gap-6">
          <div className="ae-card p-10">
            <Target className="w-10 h-10 text-[#C5A059] mb-4" />
            <h3 className="text-2xl font-medium text-[#0F2A4A] mb-3">Our Mission</h3>
            <p className="text-slate-600 leading-relaxed">To connect exceptional talent with exceptional opportunities — through deep industry expertise, ethical practices, and technology-first recruitment.</p>
          </div>
          <div className="ae-card p-10">
            <Eye className="w-10 h-10 text-[#C5A059] mb-4" />
            <h3 className="text-2xl font-medium text-[#0F2A4A] mb-3">Our Vision</h3>
            <p className="text-slate-600 leading-relaxed">To be India's most trusted recruitment consultancy — the first partner CHROs call, and the first career move every professional considers.</p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="ae-container">
          <div className="text-center mb-14">
            <div className="ae-overline mb-3">Core Values</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">What we <span className="font-serif italic text-[#C5A059]">stand for</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} className="ae-card p-8" data-testid={`value-${i}`}>
                <v.icon className="w-8 h-8 text-[#C5A059] mb-4" />
                <h3 className="font-heading font-medium text-lg text-[#0F2A4A] mb-2">{v.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recruitment process */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="ae-container">
          <div className="text-center mb-14">
            <div className="ae-overline mb-3">Our Process</div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#0F2A4A]">The <span className="font-serif italic text-[#C5A059]">Achievers Way</span></h2>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {PROCESS.map((p) => (
              <div key={p.n} className="ae-card p-6" data-testid={`process-${p.n}`}>
                <div className="font-serif text-4xl text-[#C5A059] mb-3">{p.n}</div>
                <h3 className="font-heading font-medium text-[#0F2A4A] mb-2 text-base">{p.t}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Message */}
      <section className="py-24 bg-white">
        <div className="ae-container max-w-4xl">
          <div className="ae-card p-12 relative">
            <div className="font-serif text-8xl text-[#C5A059]/20 absolute top-4 left-8">"</div>
            <div className="relative">
              <div className="ae-overline mb-4">Founder Message</div>
              <p className="text-xl md:text-2xl font-serif italic text-[#0F2A4A] leading-relaxed">
                We didn't build Achievers to fill vacancies. We built it to change lives — one great match at a time. Every placement is a story. And we take that responsibility seriously.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#0F2A4A] flex items-center justify-center text-[#C5A059] font-heading text-xl">AE</div>
                <div>
                  <div className="font-heading font-medium text-[#0F2A4A]">Founder & CEO</div>
                  <div className="text-sm text-slate-500">Achievers Enterprise</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
